import React from 'react';

import { faCheckSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { LIST_PARKING_MEMBERSHIPS_RESOURCE } from 'src/features/users/api/useListParkingsMemberships';
import { useUpdateParkingMembership } from 'src/features/users/api/useUpdateParkingMembership';
import { userMessages } from 'src/features/users/utils/messages';
import { Membership, MEMBERSHIP_STATUS } from 'src/features/users/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { generalErrorMessages } from 'src/utils/messages';

interface UserFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUser: Membership;
    children?: React.ReactNode;
}

function ActivateUserFormModal<TData>({ selectedUser, children }: UserFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: updateUser, isPending: isPendingActivate } = useUpdateParkingMembership({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PARKING_MEMBERSHIPS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(userMessages.userActivationSuccess),
                description: formatMessage(userMessages.userActivationSuccessDescription, {
                    entry: selectedUser.user.name,
                }),
            });
        },
        onError: () => {
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: formatMessage(generalErrorMessages.unknownError),
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => updateUser({ membershipId: selectedUser.id, parkingId, status: MEMBERSHIP_STATUS.ACTIVE });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faCheckSquare} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage {...userMessages.activateUser} />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={userMessages.activateUserSubtitle.id}
                                    defaultMessage={userMessages.activateUserSubtitle.defaultMessage}
                                    values={{
                                        entry: selectedUser.user.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={userMessages.confirmActivateUser.id}
                            defaultMessage={userMessages.confirmActivateUser.defaultMessage}
                            values={{
                                entry: selectedUser.user.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button disabled={isPendingActivate} type="submit" onClick={onSubmit} className="flex-1">
                        {isPendingActivate && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage {...userMessages.activateUser} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ActivateUserFormModal;
